// F1 App-Shell (TES-948): zentrale Navigations-Daten + Rollen-Badge-Varianten.
// Aus AppLayout.tsx extrahiert, damit SideNav / NavDrawer / BottomNav dieselbe
// Quelle teilen. Reine Daten/Typen — keine Logik, kein JSX. Routen + nav.*-Keys
// bleiben 1:1 erhalten (keine Stitch-Mock-Fiktionen ohne reales Backing — MPP ist seit
// TES-1730/TES-1731 eine echte Entität mit eigener Tabelle + RLS).
// Rollen-Anzeigetexte kommen aus i18n (roles.*), nicht hartkodiert.
import {
  LayoutDashboard,
  ClipboardList,
  ClipboardCheck,
  FileCheck,
  AlertTriangle,
  History,
  ListTodo,
  Database,
  Wrench,
  TestTube2,
  FlaskConical,
  Palette,
  GitBranch,
  Server,
  Bell,
  Layers,
  BookOpen,
  Key,
  Users,
  Building,
  CalendarClock,
  FileText,
  Shield,
  ShieldCheck,
  Activity,
  Settings2,
  Bot,
  ScrollText,
  Cog,
  Workflow,
  UserCog,
  GitMerge,
  DatabaseZap,
} from "lucide-react";

export type NavItem = {
  nameKey: string;
  href: string;
  icon: typeof LayoutDashboard;
  roles?: ("admin" | "qualitaetsmanager" | "mangelbearbeiter" | "tester" | "customer_approver")[];
  offlineEnabled?: boolean;
};

// Main navigation - reordered per requirements.
// ADR-CHK-002 (TES-1535): super_admin in admin konsolidiert. admin ist Plattform-Admin
// OHNE operativen Zugriff und erscheint daher NICHT in den operativen Nav-Rollenlisten.
// TES-2008 (LH v2.3 „IT-Admin = nothing operative"): admin ist aus JEDER operativen
// roles-Liste raus — auch aus nav.history (vormals toter Code: useAuth setzt isSuperAdmin =
// hasRole("admin"), useShellModel isTenantContext = isSuperAdmin && active_tenant_id → ein
// tenant-scoped Admin läuft im Guest-Mode-Kontext, useNavSections unterdrückt die operative
// Sektion komplett; die roles:["admin"]-Deklaration auf /history erreichte den Admin nie).
// Der Governance-Read auf die Historie ist über /admin/audit-trail (adminNavigation)
// abgedeckt. Operative Sichtbarkeit regelt RLS.
export const navigation: NavItem[] = [
  {
    nameKey: "nav.dashboard",
    href: "/",
    icon: LayoutDashboard,
    roles: ["qualitaetsmanager", "mangelbearbeiter", "tester", "customer_approver"],
  },
  {
    nameKey: "nav.tasks",
    href: "/tasks",
    icon: ListTodo,
    roles: ["qualitaetsmanager", "mangelbearbeiter", "tester"],
    offlineEnabled: true,
  },
  {
    nameKey: "nav.inspections",
    href: "/inspections",
    icon: FileCheck,
    roles: ["qualitaetsmanager", "tester", "customer_approver"],
    offlineEnabled: true,
  },
  {
    nameKey: "nav.defects",
    href: "/defects",
    icon: AlertTriangle,
    roles: ["qualitaetsmanager", "mangelbearbeiter", "tester", "customer_approver"],
    offlineEnabled: true,
  },
  {
    nameKey: "nav.checklists",
    href: "/checklists",
    icon: ClipboardList,
    roles: ["qualitaetsmanager", "tester", "customer_approver"],
    offlineEnabled: true,
  },
  {
    nameKey: "nav.schedules",
    href: "/schedules",
    icon: CalendarClock,
    roles: ["qualitaetsmanager", "tester"],
  },
  {
    // TES-1731: Maschinenprüfplanung (MPP) — Einstiegspunkt, klar getrennt von „Checklisten"
    // (Vorlagen) und „Prüfpläne" (Schedules). Nav für die operativen Planer-Rollen (QM + Tester,
    // analog zu „Prüfpläne"). admin = mpp:view 'none'. Read-only-Rollen (Mangelbearbeiter/Kunde,
    // mpp:view 'limited') erreichen die Seite kontextuell (Route = excludedRoles ["admin"]), ohne
    // sie als Haupt-Nav-Ziel zu führen.
    nameKey: "nav.mpp",
    href: "/mpp",
    icon: ClipboardCheck,
    roles: ["qualitaetsmanager", "tester"],
  },
  {
    // TES-2008: „admin" hier entfernt (LH v2.3 „IT-Admin = nothing operative"). Der Admin
    // liest die Historie über /admin/audit-trail (adminNavigation, Governance-Read TES-1621),
    // nicht über die operative /history-Sektion.
    nameKey: "nav.history",
    href: "/history",
    icon: History,
    roles: ["qualitaetsmanager", "mangelbearbeiter", "tester"],
  },
  {
    // TES-1975: read-only „Gruppen"-Liste für die operativen Rollen (v2.3: „alle Gruppen für alle
    // lesbar"). Verwaltung (CRUD) ist qm_admin-only (canManageGroups) — die Seite rendert read-only,
    // wenn keine Gruppen-Verwaltungsrechte vorliegen. team_group-SELECT ist tenant-weit (Mig 329).
    // TES-2048 (ADR 0010): „Gruppen" zeigt auf die standort-first Seite /admin/groups —
    // /admin/teams bleibt als Route für die TEAM-Mitglieder-Verwaltung (Übergang) bestehen.
    nameKey: "nav.groups",
    href: "/admin/groups",
    icon: Users,
    roles: ["qualitaetsmanager", "mangelbearbeiter", "tester"],
  },
];

// QM-specific navigation (visible to qualitaetsmanager only).
// TES-1658: QM ist die operative Rolle — Maschinen, Teams, Workflow-Builder,
// Externer Zugriff und Konflikte gehören hierher (NICHT in die Admin-Gruppe).
// `/admin/roles` ist KEIN QM-Item mehr (RBAC-CRUD ist plattform-/admin-only).
export const qmNavigation: NavItem[] = [
  { nameKey: "nav.positions", href: "/admin/positions", icon: Building },
  { nameKey: "nav.machines", href: "/admin/machines", icon: Cog },
  { nameKey: "nav.sapStammdatenSync", href: "/sap/stammdaten-sync", icon: DatabaseZap },
  { nameKey: "nav.pdfProfiles", href: "/admin/pdf-profiles", icon: FileText },
  // TES-1975: „Teams" aus der QM-Sicht entfernt — Gruppen-Verwaltung lebt unter qm_admin; alle anderen
  // Rollen sehen eine read-only „Gruppen"-Liste (nav.groups in `navigation`).
  { nameKey: "nav.workflowBuilder", href: "/admin/workflow-builder", icon: Workflow },
  { nameKey: "nav.externalAccess", href: "/admin/external-access", icon: UserCog },
  { nameKey: "nav.conflicts", href: "/admin/conflicts", icon: GitMerge },
  { nameKey: "nav.auditTrail", href: "/admin/audit-trail", icon: ScrollText },
];

// qm_admin (Org-Hülle, v2.3 / TES-1973 — schlank seit TES-2101): Owner-Entscheid 2026-07-06 —
// qm_admin ist NICHT operativ. KEIN Dashboard, KEINE geklonten operativen Menüs (Prüfungen/Mängel/
// Maschinen/MPP/Prüfpläne/Prüfobjekte/Historie sind hier RAUS). Bleibt: die „braucht Aktion"-
// Arbeitsliste als Startfläche + die Org-Hüllen (Gruppen-Verwaltung, Benutzerverwaltung). Die
// RLS-Global-Read-Sicht (Mig 366) ist Daten-/RLS-Ebene, keine Sidebar-Begründung mehr — Objekt-
// Zugriff (z.B. /inspections, /defects) bleibt für qm_admin per Deep-Link aus der Arbeitsliste
// erreichbar (RLS unverändert), nur ohne eigenen Sidebar-Eintrag. „Rollen" (/admin/roles) bleibt
// plattform-/admin-only (RBAC-CRUD, App.tsx ~Zeile 368-370 — <ProtectedRoute
// requiredRoles={["admin"]}>); ein qm_admin-Zugriff bräuchte eine Route-Guard-Änderung und ist
// hier bewusst NICHT aufgenommen.
// Anlass-Typen/Neuzuweisung/Bulk-Import/Kundenabnahme-Export haben weiterhin (noch) keine eigene
// Route → Follow-up-Issue (R-SCOPE-01), NICHT im selben PR improvisiert.
export const qmAdminNavigation: NavItem[] = [
  { nameKey: "nav.tasks", href: "/tasks", icon: ListTodo }, // „braucht Aktion"-Arbeitsliste, Startfläche (TES-2101)
  { nameKey: "nav.groups", href: "/admin/groups", icon: Users }, // Gruppen-Verwaltung standort-first (qm_admin, TES-2048)
  { nameKey: "nav.users", href: "/admin/users", icon: UserCog }, // Stammdaten-Pflege job_title/department/standort (TES-2056)
];

// Tenant Admin navigation - visible to tenant admins
export const tenantAdminNavigation: NavItem[] = [
  { nameKey: "nav.tenantSettings", href: "/admin/tenant-settings", icon: Settings2 },
];

// Super Admin navigation - visible only to super admins
export const superAdminNavigation: NavItem[] = [
  { nameKey: "nav.allUsers", href: "/admin/all-users", icon: Users },
  { nameKey: "nav.notificationOverview", href: "/admin/notification-overview", icon: Bell },
  { nameKey: "nav.environments", href: "/admin/environments", icon: Layers },
  { nameKey: "nav.setupStatus", href: "/admin/setup-status", icon: Activity },
  { nameKey: "nav.memoryDocs", href: "/admin/memory-docs", icon: FileText },
];

// Admin-only navigation — Plattform-Verwaltung (User + Rollen/Rechte + Governance).
// TES-1658: Admin verwaltet NUR Benutzer, Rollen & Berechtigungen, Vorlagen-Stewards
// und liest den Audit-Trail (Governance-Read, TES-1621). Operative Items (Maschinen,
// Teams, Workflow-Builder, Externer Zugriff, Konflikte) liegen in der QM-Sektion.
export const adminNavigation: NavItem[] = [
  { nameKey: "nav.users", href: "/admin/users", icon: Users },
  { nameKey: "nav.roles", href: "/admin/roles", icon: Shield },
  { nameKey: "nav.templateStewards", href: "/admin/template-stewards", icon: ShieldCheck },
  { nameKey: "nav.auditTrail", href: "/admin/audit-trail", icon: ScrollText },
];

// Platform tools navigation - only for super admins (tenant-independent)
export const platformToolsNavigation: NavItem[] = [
  { nameKey: "nav.aiSettings", href: "/admin/ai-settings", icon: Bot },
  { nameKey: "nav.ssoSettings", href: "/admin/sso-settings", icon: ShieldCheck },
  { nameKey: "nav.rlsPolicies", href: "/admin/rls-policies", icon: Database },
  { nameKey: "nav.rlsTestCoverage", href: "/admin/rls-test-coverage", icon: TestTube2 },
  { nameKey: "nav.e2eCoverage", href: "/admin/e2e-coverage", icon: FlaskConical },
  { nameKey: "nav.githubActions", href: "/admin/github-actions", icon: GitBranch },
  { nameKey: "nav.edgeFunctions", href: "/admin/edge-functions", icon: Server },
  { nameKey: "nav.secretsDocs", href: "/admin/secrets-docs", icon: Key },
  { nameKey: "nav.apiDocs", href: "/admin/api-docs", icon: BookOpen },
  { nameKey: "nav.sentryTraces", href: "/admin/sentry-traces", icon: Activity },
  { nameKey: "nav.uiPatterns", href: "/admin/ui-patterns", icon: Palette },
  { nameKey: "nav.devTools", href: "/admin/dev-tools", icon: Wrench },
];

// HILFE — Benutzeranleitung. Sichtbar NUR für operative Rollen (QM/Tester/Mangelbearbeiter/
// Kunde) + Super-Admin, NICHT für den reinen Plattform-Admin (ADR-CHK-002: admin hat keinen
// operativen Zugriff). Eigene Sektion statt am Admin-Block klebend (TES-1683).
// Nur intern (Breadcrumb-SoT) — die Guide-Links in SideNav/NavDrawer sind bespoke
// (data-tour="help-button"), daher kein Export nötig.
const helpNavigation: NavItem[] = [{ nameKey: "nav.userGuide", href: "/guide", icon: BookOpen }];

// Operative Rollen (ADR-CHK-002). Der reine `admin` zählt NICHT als operativ.
const OPERATIVE_ROLES = ["qualitaetsmanager", "mangelbearbeiter", "tester", "customer_approver"];

// true, wenn der Nutzer mindestens eine operative Rolle hat (steuert die HILFE-Sichtbarkeit).
export function hasOperativeRole(roles: string[]): boolean {
  return roles.some((r) => OPERATIVE_ROLES.includes(r));
}

// ADR-CHK-002: super_admin consolidated into admin.
// TES-1886 / GOAL A1 (v2.3): qm_admin is the 5th canonical backbone role. It is role_id-only
// (roles.key='qm_admin') — NOT an app_role enum value — but useAuthQueries surfaces it into the
// frontend roles[] (see useAuthQueries.AppRole). It must appear here as a display role so
// resolvePrimaryRole / roleBadge* don't fall back to the "tester" default for a qm_admin user.
export type AppRole =
  "admin" | "qm_admin" | "qualitaetsmanager" | "mangelbearbeiter" | "tester" | "customer_approver";

// Rollen-Labels werden ueber i18n (`t('roles.${primaryRole}')`) aufgeloest, nicht hartkodiert
// (siehe UserMenu/NavDrawer). roles.*-Keys liegen in src/i18n/locales/{de,en,ja}.json.
export const roleBadgeVariants: Record<AppRole, string> = {
  admin: "bg-primary/20 text-primary border-primary/30",
  qm_admin: "bg-indigo-500/20 text-indigo-700 border-indigo-500/30 dark:text-indigo-200",
  qualitaetsmanager: "bg-purple-500/20 text-purple-900 border-purple-500/30 dark:text-purple-200",
  mangelbearbeiter: "bg-cyan-500/20 text-cyan-600 border-cyan-500/30",
  tester: "bg-success/20 text-success border-success/30",
  customer_approver: "bg-secondary/20 text-secondary-foreground border-secondary/30",
};

export const sidebarRoleBadgeVariants: Record<AppRole, string> = {
  admin: "bg-primary/20 text-sidebar-foreground border-sidebar-foreground/30",
  qm_admin: "bg-indigo-500/20 text-indigo-100 border-indigo-100/30",
  qualitaetsmanager: "bg-purple-500/20 text-purple-100 border-purple-100/30",
  mangelbearbeiter: "bg-cyan-500/20 text-cyan-100 border-cyan-100/30",
  tester: "bg-success/20 text-green-100 border-green-100/30",
  customer_approver: "bg-secondary/20 text-yellow-50 border-yellow-50/30",
};

// data-tour Attribut je Haupt-Route (GuideTour / E2E-Selektoren — verlustfrei erhalten).
// TES-1701: der GuideTour-Schritt „Teams" (useGuideTourSteps) braucht einen gemounteten
// `nav-teams`-Anker, sonst wird er still uebersprungen. Seit TES-2048 haengt der Anker am
// „Gruppen"-Eintrag (/admin/groups) — demselben Nav-Item, das vor TES-1975/2048 „Teams" war;
// /admin/teams hat keinen Sidebar-Eintrag mehr (Route bleibt fuer die Mitglieder-Verwaltung).
const DATA_TOUR_BY_HREF: Record<string, string> = {
  "/": "nav-dashboard",
  "/tasks": "nav-tasks",
  "/inspections": "nav-inspections",
  "/defects": "nav-defects",
  "/admin/groups": "nav-teams",
};

export function navDataTour(href: string): string | undefined {
  return DATA_TOUR_BY_HREF[href];
}

// ── Breadcrumb-SoT (TES-1683) ──────────────────────────────────────────────
// Breadcrumb-Label + Parent werden aus DERSELBEN Nav-Quelle abgeleitet wie die Sidebar:
// Sidebar-Sektion == Breadcrumb-Parent. Damit hat jede Nav-Route automatisch eine korrekte
// Breadcrumb und es gibt kein driftendes, hartcodiertes Zweit-Mapping mehr (AppBreadcrumb).
export interface BreadcrumbRoute {
  /** i18n-Key des Seiten-Labels. */
  labelKey: string;
  /** i18n-Key des Parent-Sektion-Labels, oder null für Top-Level-Routen. */
  parentLabelKey: string | null;
}

const BREADCRUMB_NAV_GROUPS: { items: NavItem[]; parentLabelKey: string | null }[] = [
  { items: navigation, parentLabelKey: null },
  { items: qmNavigation, parentLabelKey: "nav.qm" },
  { items: tenantAdminNavigation, parentLabelKey: "nav.administration" },
  { items: adminNavigation, parentLabelKey: "nav.administration" },
  { items: superAdminNavigation, parentLabelKey: "nav.superAdmin" },
  { items: platformToolsNavigation, parentLabelKey: "nav.platform" },
  { items: helpNavigation, parentLabelKey: "nav.help" },
];

// Erster Treffer gewinnt (z.B. /admin/audit-trail steht in QM- UND Admin-Gruppe → QM).
const BREADCRUMB_BY_PATH: Record<string, BreadcrumbRoute> = (() => {
  const map: Record<string, BreadcrumbRoute> = {};
  for (const group of BREADCRUMB_NAV_GROUPS) {
    for (const item of group.items) {
      if (!map[item.href]) {
        map[item.href] = { labelKey: item.nameKey, parentLabelKey: group.parentLabelKey };
      }
    }
  }
  // Routen ohne Sidebar-Eintrag (über Aktionen/Menüs erreichbar).
  map["/notifications"] = { labelKey: "notifications.title", parentLabelKey: null };
  map["/profile"] = { labelKey: "nav.profile", parentLabelKey: null };
  // TES-2048: /admin/teams (Team-Mitglieder-Verwaltung, Übergang) hat keinen Sidebar-Eintrag
  // mehr — erreichbar über das Super-Admin-Dashboard-Tile; Breadcrumb bleibt korrekt.
  map["/admin/teams"] = { labelKey: "nav.teams", parentLabelKey: null };
  return map;
})();

// Breadcrumb-Konfiguration für eine statische Route (undefined = keine Breadcrumb).
export function breadcrumbForPath(path: string): BreadcrumbRoute | undefined {
  return BREADCRUMB_BY_PATH[path];
}

// Wichtigste Rolle fuer die Anzeige (hoehere Rollen gewinnen) — 1:1 aus AppLayout.
// TES-1886: qm_admin rangiert direkt unter admin und über QM (Rang nahe admin/QM),
// damit resolvePrimaryRole(["qm_admin"]) qm_admin liefert statt auf "tester" zu defaulten.
const ROLE_PRIORITY: AppRole[] = [
  "admin",
  "qm_admin",
  "qualitaetsmanager",
  "tester",
  "mangelbearbeiter",
  "customer_approver",
];

export function resolvePrimaryRole(roles: string[]): AppRole {
  return ROLE_PRIORITY.find((r) => roles.includes(r)) ?? "tester";
}

// Anzeigename + Initialen aus dem Profil ableiten — 1:1 aus AppLayout.tsx.
type ProfileLike =
  { first_name?: string | null; last_name?: string | null; email: string } | null | undefined;

// `?? ''` (statt `|| ''`) ist hier verlustfrei — first_name/last_name sind string|null|undefined,
// und ein leerer String wird durch .trim() ohnehin verworfen. Das vermeidet die zusaetzlichen
// Falsy-Verzweigungen der `||`-Kette (Komplexitaet) ohne Verhaltensaenderung.
function namePart(value: string | null | undefined): string {
  return value ?? "";
}

function firstChar(value: string | null | undefined): string {
  return namePart(value).charAt(0);
}

export function resolveDisplayName(profile: ProfileLike): string {
  if (!profile) return "Benutzer";
  const full = `${namePart(profile.first_name)} ${namePart(profile.last_name)}`.trim();
  return full || profile.email;
}

export function resolveInitials(profile: ProfileLike): string {
  if (!profile) return "U";
  const fromName = `${firstChar(profile.first_name)}${firstChar(profile.last_name)}`.toUpperCase();
  return fromName || profile.email.charAt(0).toUpperCase();
}
